import motion from '@lib/motion';
import { layoutRecalcManager, getObserver, createSlider, motionReduced, layoutPhaseMeasure, layoutPhaseApply } from '@theme/theme-global';

const VISIBLE_COUNT = 4;

class LookbookCarousel extends HTMLElement {
  constructor() {
    super();
    this.section = null;
    this.leftViewport = null;
    this.leftTrack = null;
    this.leftImages = [];
    this.rightViewport = null;
    this.rightTrack = null;
    this.scrollDistance = 0;
    this.rightOverflow = 0;
    this.count = 0;
    this.viewportWidth = 1;
    this.gapPx = 10;
    this.trackStepPx = 1;
    this.totalTrackTranslate = 1;
    this.flexMaxPct = 33;
    this.flexMinPct = 18;
    this.flexStepPct = 5;
    this.ticking = false;
    this._active = false;
    this._cssCached = false;
    this._observer = null;
    this.measure = this.measure.bind(this);
    this.update = this.update.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('[data-lookbook-main]');
    this.leftViewport = this.querySelector('[data-lookbook-left-viewport]');
    this.leftTrack = this.querySelector('[data-lookbook-left]');
    this.leftImages = [...this.querySelectorAll('[data-lookbook-image]')];
    this.rightViewport = this.querySelector('[data-lookbook-right]');
    this.rightTrack = this.querySelector('[data-lookbook-products]');
    this.count = this.leftImages.length;

    if (
      !this.section ||
      !this.leftViewport ||
      !this.leftTrack ||
      !this.rightViewport ||
      !this.rightTrack ||
      this.count < 1
    ) {
      return;
    }

    if ('IntersectionObserver' in window) {
      this._observer = getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false);
      this._observer.observe(this, () => this._activate());
    } else {
      this._activate();
    }
  }

  disconnectedCallback() {
    this._observer?.unobserve(this);
    this._observer = null;
    layoutRecalcManager.unregister(this.measure);
    window.removeEventListener('scroll', this.onScroll);
    this._active = false;
    if (this.section) this.section.style.height = '';
    if (this.leftTrack) this.leftTrack.style.transform = '';
    if (this.rightTrack) this.rightTrack.style.transform = '';
    this.leftImages.forEach((el) => {
      el.style.flex = '';
      el.style.opacity = '';

      const image = el.querySelector('img, svg');
      if (image) {
        image.style.filter = '';
      }
    });
  }

  _activate() {
    if (this._active) return;
    this._active = true;

    this.leftTrack.style.willChange = 'transform';
    this.rightTrack.style.willChange = 'transform';

    layoutRecalcManager.register(this.measure, { root: this });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    layoutRecalcManager.request(this.measure);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  measure(phase) {
    if (!this.section || this.count < 1 || !this._active) return;

    if (layoutPhaseMeasure(phase)) {
      const viewportHeight = window.innerHeight;
      const rightHeight = this.rightTrack.scrollHeight;
      const rightVisible = this.rightViewport.clientHeight;
      this.rightOverflow = Math.max(0, rightHeight - rightVisible);

      this.readMetrics({ refreshCss: true });
      this.scrollDistance = Math.max(this.rightOverflow, this.totalTrackTranslate, 1);
      this._pendingSectionHeight = `${this.scrollDistance + viewportHeight}px`;
    }

    if (layoutPhaseApply(phase)) {
      if (this._pendingSectionHeight) {
        this.section.style.height = this._pendingSectionHeight;
      }
      this.update({ skipMetrics: true });
    }
  }

  _ensureFlexMetrics(force = false) {
    if (!force && this._cssCached) return;
    this.gapPx = this.getImageGap();
    this.flexStepPct = this.getCssNumber('--lookbook-flex-step', 5);
    this.flexMaxPct = this.getCssNumber('--lookbook-flex-front', 33);
    this.flexMinPct = this.flexMaxPct - this.flexStepPct * (VISIBLE_COUNT - 1);
    this._cssCached = true;
  }

  readMetrics({ refreshCss = false } = {}) {
    this.viewportWidth = this.leftViewport.clientWidth || 1;
    this._ensureFlexMetrics(refreshCss);

    if (this.count > 1) {
      const first = this.leftImages[0];
      const last = this.leftImages[this.count - 1];
      this.totalTrackTranslate = last.offsetLeft - first.offsetLeft;
    } else {
      this.totalTrackTranslate = 0;
    }
  }

  getImageGap() {
    const raw = getComputedStyle(this.leftTrack).getPropertyValue('--lookbookImageGap').trim();
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : 10;
  }

  getCssNumber(property, fallback) {
    const raw = getComputedStyle(this.leftTrack).getPropertyValue(property).trim();
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
  }

  getProgress() {
    const top = this.section.getBoundingClientRect().top;
    if (top > 0) return 0;
    return Math.min(1, Math.max(0, -top / this.scrollDistance));
  }

  getBlockIndex(el) {
    const index = Number.parseInt(el.dataset.index, 10);
    return Number.isNaN(index) ? 0 : index;
  }

  /** Single scroll driver — flex + track translate both derive from this. */
  getTravel(progress) {
    return progress * Math.max(this.count - 1, 1);
  }

  getRank(blockIndex, travel) {
    return blockIndex - travel;
  }

  getFlexPct(rank) {
    if (rank < 0) {
      return this.flexMaxPct;
    }

    return Math.max(this.flexMinPct, this.flexMaxPct - rank * this.flexStepPct);
  }

  getOpacity1(flexPct) {
    //opcity with progress
    let progress = (flexPct - this.flexMinPct) / (this.flexMaxPct - this.flexMinPct);

    progress = Math.max(0, Math.min(1, progress));

    // Ease Out Cubic
    progress = 1 - Math.pow(1 - progress, 3);

    return 0.3 + progress * 0.7;
  }

  getOpacity(flexPct) {
    const stops = [
      [18, 0.3],
      [21, 0.3],
      [24, 0.35],
      [27, 0.5],
      [30, 0.6],
      [33, 1.0],
    ];

    if (flexPct <= stops[0][0]) return stops[0][1];
    if (flexPct >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];

    for (let i = 0; i < stops.length - 1; i++) {
      const [x1, y1] = stops[i];
      const [x2, y2] = stops[i + 1];

      if (flexPct >= x1 && flexPct <= x2) {
        const t = (flexPct - x1) / (x2 - x1);
        return y1 + (y2 - y1) * t;
      }
    }

    return 1;
  }

  getBlur(flexPct) {
    const stops = [
      [18, 3.5],
      [21, 3],
      [24, 2],
      [27, 1],
      [30, 0],
      [33, 0],
    ];

    if (flexPct <= stops[0][0]) return stops[0][1];
    if (flexPct >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];

    for (let i = 0; i < stops.length - 1; i++) {
      const [x1, y1] = stops[i];
      const [x2, y2] = stops[i + 1];

      if (flexPct >= x1 && flexPct <= x2) {
        const t = (flexPct - x1) / (x2 - x1);
        return y1 + (y2 - y1) * t;
      }
    }

    return 0;
  }

  getTrackTranslate(progress) {
    return progress * this.totalTrackTranslate;
  }

  update({ skipMetrics = false } = {}) {
    if (!this.section || this.count < 1 || !this._active) return;

    if (!skipMetrics) this.readMetrics();

    const progress = this.getProgress();
    const travel = this.getTravel(progress);
    // const trackX = this.getTrackTranslate(travel);
    const trackX = this.getTrackTranslate(progress);

    this.leftImages.forEach((el) => {
      const rank = this.getRank(this.getBlockIndex(el), travel);

      // Keep minimum width instead of collapsing to 0
      if (rank > VISIBLE_COUNT + 1) {
        el.style.flex = `0 0 ${this.flexMinPct}%`;
        el.style.opacity = '0';
        el.style.pointerEvents = 'none';
        return;
      }

      const flexPct = this.getFlexPct(rank);
      const opacity = this.getOpacity(flexPct);
      const blur = this.getBlur(flexPct);
      el.style.flex = `0 0 ${flexPct}%`;
      el.style.opacity = opacity;
      // el.style.filter = `blur(${blur}px)`;
      const image = el.querySelector('img, svg');
      if (image) {
        image.style.filter = `blur(${blur}px)`;
      }
      el.style.pointerEvents = rank >= 0 && rank < 0.5 ? '' : 'none';
      el.style.zIndex = String(VISIBLE_COUNT - Math.round(Math.max(0, Math.min(rank, VISIBLE_COUNT - 1))));
    });

    this.leftTrack.style.transform = `translate3d(${trackX}px, 0, 0)`;

    // this.leftTrack.style.transform = `translate3d(${-trackX}px,0,0)`;
    const rightY = -progress * this.rightOverflow;
    this.rightTrack.style.transform = `translate3d(0, ${rightY}px, 0)`;
  }
}

if (!customElements.get('lookbook-carousel')) {
  customElements.define('lookbook-carousel', LookbookCarousel);
}


class LookbookCarouselMobile extends HTMLElement {
  constructor() {
    super();
    this.section = null;
    this.imagesEl = null;
    this.productsEl = null;
    this.productPanels = [];
    this.imagesInstance = null;
    this._observer = null;
    this._productsAnimation = null;
    this._productsOpen = false;
    this._slideSyncBound = false;
    this._onImageSlideSettled = null;
    this._activeLookIndex = 0;
    this._productsMaxHeight = 0;
    this._stickySuccessRaf = null;
    this._heightRegistered = false;
    this.onActionClick = this.onActionClick.bind(this);
    this.equalizeProductsHeight = this.equalizeProductsHeight.bind(this);
    this.onStickySuccessShow = this.onStickySuccessShow.bind(this);
    this.onStickySuccessHide = this.onStickySuccessHide.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.imagesEl = this.querySelector('[data-lookbook-images-mobile]');
    this.productsEl = this.querySelector('[data-lookbook-products-mobile]');
    this.stickyWrapper = this.querySelector('.lookbook-mobile-sticky-wrapper');
    this.productPanels = [...(this.productsEl?.querySelectorAll('[data-lookbook-products-panel]') || [])];
    this._productsOpen = this.productsEl ? !this.productsEl.classList.contains('hidden') : false;
    this.addEventListener('click', this.onActionClick);
    document.addEventListener('sticky-atc-success:show', this.onStickySuccessShow);
    document.addEventListener('sticky-atc-success:hide', this.onStickySuccessHide);
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onActionClick);
    document.removeEventListener('sticky-atc-success:show', this.onStickySuccessShow);
    document.removeEventListener('sticky-atc-success:hide', this.onStickySuccessHide);
    this.cancelStickySuccessMeasure();
    this.clearStickySuccessOffset();
    layoutRecalcManager.unregister(this.equalizeProductsHeight);
    this._heightRegistered = false;
    this.stopProductsAnimation();
    if (this._observer) {
      this._observer.unobserve?.(this);
      this._observer = null;
    }
    this.unbindSlideSync();
    if (this.imagesInstance?.destroy) {
      this.imagesInstance.destroy(true, true);
    }
    this.imagesInstance = null;
  }

  onStickySuccessShow() {
    try {
      this.applyStickySuccessOffset();
    } catch (_) {
      this.clearStickySuccessOffset();
    }
  }

  onStickySuccessHide() {
    try {
      this.cancelStickySuccessMeasure();
      this.clearStickySuccessOffset();
    } catch (_) {
      // Keep lookbook usable even if offset cleanup fails.
    }
  }

  cancelStickySuccessMeasure() {
    if (this._stickySuccessRaf == null) return;
    cancelAnimationFrame(this._stickySuccessRaf);
    this._stickySuccessRaf = null;
  }

  applyStickySuccessOffset() {
    if (!this.stickyWrapper) return;

    const apply = () => {
      this._stickySuccessRaf = null;
      try {
        const successEl =
          document.querySelector('sticky-add-to-cart-success.is-visible') ||
          document.querySelector('sticky-add-to-cart-success');
        if (!successEl) {
          this.clearStickySuccessOffset();
          return;
        }

        const height = Math.ceil(successEl.getBoundingClientRect().height || successEl.offsetHeight || 0);
        if (height <= 0) {
          this.clearStickySuccessOffset();
          return;
        }

        this.stickyWrapper.style.setProperty('--lookbook-success-offset', `${height}px`);
        this.stickyWrapper.classList.add('has-sticky-success-offset');
      } catch (_) {
        this.clearStickySuccessOffset();
      }
    };

    apply();
    this.cancelStickySuccessMeasure();
    this._stickySuccessRaf = requestAnimationFrame(() => {
      this._stickySuccessRaf = requestAnimationFrame(apply);
    });
  }

  clearStickySuccessOffset() {
    if (!this.stickyWrapper) return;
    this.stickyWrapper.style.setProperty('--lookbook-success-offset', '0px');
    this.stickyWrapper.classList.remove('has-sticky-success-offset');
  }

  init() {
    const sectionIndex = parseInt(this.section?.querySelector('[data-index]')?.dataset?.index ?? '1', 10);
    const aboveTheFoldSection = this.section && sectionIndex <= 2;

    const setupSliders = async () => {
      if (this.imagesInstance || this._sliderInitializing) return;
      if (!this.imagesEl) return;

      this._sliderInitializing = true;
      this._ensureHeightListener();
      try {
        this.imagesInstance = await createSlider(this.imagesEl, JSON.parse(this.imagesEl.dataset.params || '{}'));
        this.bindSlideSync();
        this.syncProductsToImages();
      } finally {
        this._sliderInitializing = false;
      }
    };

    if (aboveTheFoldSection) {
      setupSliders();
    } else if ('IntersectionObserver' in window) {
      this._observer = getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false);
      this._observer.observe(this, setupSliders);
    } else {
      setupSliders();
    }
  }

  bindSlideSync() {
    if (!this.imagesInstance || this._slideSyncBound) return;

    this._slideSyncBound = true;
    this._onImageSlideSettled = () => {
      // Defer so coverflow/loop can commit realIndex before we read it
      requestAnimationFrame(() => this.syncProductsToImages());
    };

    // coverflow + loop often skips transitionEnd — listen to multiple settle signals
    this.imagesInstance.on('slideChange', this._onImageSlideSettled);
    this.imagesInstance.on('slideChangeTransitionEnd', this._onImageSlideSettled);
    this.imagesInstance.on('touchEnd', this._onImageSlideSettled);
  }

  unbindSlideSync() {
    if (!this.imagesInstance || !this._onImageSlideSettled) {
      this._slideSyncBound = false;
      this._onImageSlideSettled = null;
      return;
    }

    this.imagesInstance.off('slideChange', this._onImageSlideSettled);
    this.imagesInstance.off('slideChangeTransitionEnd', this._onImageSlideSettled);
    this.imagesInstance.off('touchEnd', this._onImageSlideSettled);
    this._slideSyncBound = false;
    this._onImageSlideSettled = null;
  }

  getActiveLookIndex() {
    if (!this.imagesInstance) return this._activeLookIndex;

    // With loop/coverflow, realIndex is the only reliable look index
    if (typeof this.imagesInstance.realIndex === 'number') {
      return this.imagesInstance.realIndex;
    }

    const activeSlide = this.imagesEl?.querySelector('.swiper-slide-active');
    const dataIndex = Number.parseInt(activeSlide?.getAttribute('data-index'), 10);
    if (Number.isFinite(dataIndex)) return dataIndex;

    return this.imagesInstance.activeIndex ?? 0;
  }

  _ensureHeightListener() {
    if (this._heightRegistered) return;
    this._heightRegistered = true;
    layoutRecalcManager.register(this.equalizeProductsHeight, { root: this });
  }

  syncProductsToImages() {
    if (!this.productPanels.length) return;

    const index = this.getActiveLookIndex();
    if (index === this._activeLookIndex) {
      const activeExists = this.productPanels.some(
        (panel) => panel.classList.contains('is-active') && Number.parseInt(panel.dataset.index, 10) === index
      );
      if (activeExists) return;
    }

    this._activeLookIndex = index;

    this.productPanels.forEach((panel) => {
      const isActive = Number.parseInt(panel.dataset.index, 10) === index;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  equalizeProductsHeight(phase) {
    // Mixed measure/write (class toggles for offscreen measure). Run once on apply or direct call.
    if (phase === 'measure') return this._productsMaxHeight || 0;

    if (!this.productsEl || !this.productPanels.length) return 0;

    const wasHidden = this.productsEl.classList.contains('hidden');
    const prevInline = {
      position: this.productsEl.style.position,
      visibility: this.productsEl.style.visibility,
      pointerEvents: this.productsEl.style.pointerEvents,
      height: this.productsEl.style.height,
      opacity: this.productsEl.style.opacity,
      overflow: this.productsEl.style.overflow,
    };

    if (wasHidden) {
      this.productsEl.classList.remove('hidden');
      this.productsEl.style.position = 'absolute';
      this.productsEl.style.visibility = 'hidden';
      this.productsEl.style.pointerEvents = 'none';
      this.productsEl.style.height = 'auto';
      this.productsEl.style.opacity = '1';
      this.productsEl.style.overflow = 'visible';
    }

    this.productsEl.style.removeProperty('--lookbook-products-height');

    this.productPanels.forEach((panel) => panel.classList.remove('is-active'));
    this.productPanels.forEach((panel) => panel.classList.add('is-measuring'));

    let maxHeight = 0;
    this.productPanels.forEach((panel) => {
      maxHeight = Math.max(maxHeight, Math.ceil(panel.scrollHeight));
    });

    this.productPanels.forEach((panel) => panel.classList.remove('is-measuring'));

    this._productsMaxHeight = maxHeight;
    if (maxHeight > 0) {
      this.productsEl.style.setProperty('--lookbook-products-height', `${maxHeight}px`);
    }

    this.productPanels.forEach((panel) => {
      const isActive = Number.parseInt(panel.dataset.index, 10) === this._activeLookIndex;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    if (wasHidden) {
      this.productsEl.classList.add('hidden');
      this.productsEl.style.position = prevInline.position;
      this.productsEl.style.visibility = prevInline.visibility;
      this.productsEl.style.pointerEvents = prevInline.pointerEvents;
      this.productsEl.style.height = prevInline.height;
      this.productsEl.style.opacity = prevInline.opacity;
      this.productsEl.style.overflow = prevInline.overflow;
    }

    return maxHeight;
  }

  onActionClick(event) {
    const button = event.target.closest('[data-button-action]');
    if (!button || !this.contains(button)) return;

    const action = button.dataset.buttonAction;
    if (action === 'toggle-products') {
      event.preventDefault();
      this.toggleProducts();
    } else if (action === 'show-products') {
      event.preventDefault();
      this.showProducts();
    } else if (action === 'hide-products') {
      event.preventDefault();
      this.hideProducts();
    }
  }

  toggleProducts() {
    if (this._productsOpen) {
      this.hideProducts();
    } else {
      this.showProducts();
    }
  }

  stopProductsAnimation() {
    if (this._productsAnimation) {
      this._productsAnimation.stop();
      this._productsAnimation = null;
    }
  }

  releaseProductsLayout() {
    if (!this.productsEl) return;
    this.productsEl.style.height = '';
    this.productsEl.style.opacity = '';
    this.productsEl.style.overflow = '';
    this.productsEl.style.minHeight = '';
  }

  measureProductsHeight() {
    if (this._productsMaxHeight > 0) return this._productsMaxHeight;
    return this.equalizeProductsHeight();
  }

  showProducts() {
    if (!this.productsEl || this._productsOpen) return;

    this._ensureHeightListener();
    this._productsOpen = true;
    this.productsEl.setAttribute('aria-hidden', 'false');

    this.syncProductsToImages();
    const endHeight = this.equalizeProductsHeight();

    // Override equalized min-height so height can animate from 0
    this.productsEl.style.minHeight = '0px';
    this.productsEl.style.overflow = 'hidden';
    this.productsEl.style.height = '0px';
    this.productsEl.style.opacity = '0';
    this.productsEl.classList.remove('hidden');
    void this.productsEl.offsetHeight;

    if (motionReduced()) {
      this.stopProductsAnimation();
      this.releaseProductsLayout();
      return;
    }

    this.stopProductsAnimation();

    this._productsAnimation = motion.animate(
      this.productsEl,
      { height: `${endHeight}px`, opacity: 1 },
      { duration: 0.22, easing: [0.7, 0, 0.2, 1] }
    );

    this._productsAnimation.finished
      .then(() => {
        if (!this._productsOpen) return;
        this.releaseProductsLayout();
        this._productsAnimation = null;
      })
      .catch(() => {
        this._productsAnimation = null;
      });
  }

  hideProducts() {
    if (!this.productsEl || !this._productsOpen) return;

    this._productsOpen = false;
    this.productsEl.setAttribute('aria-hidden', 'true');

    if (motionReduced()) {
      this.stopProductsAnimation();
      this.productsEl.classList.add('hidden');
      this.releaseProductsLayout();
      return;
    }

    this.stopProductsAnimation();

    const startHeight =
      this.productsEl.getBoundingClientRect().height || this._productsMaxHeight || this.equalizeProductsHeight();

    // Override equalized min-height so height can animate to 0
    this.productsEl.style.minHeight = '0px';
    this.productsEl.style.overflow = 'hidden';
    this.productsEl.style.height = `${startHeight}px`;
    this.productsEl.style.opacity = '1';
    void this.productsEl.offsetHeight;

    this._productsAnimation = motion.animate(
      this.productsEl,
      { height: '0px', opacity: 0 },
      { duration: 0.2, easing: [0.7, 0, 0.2, 1] }
    );

    this._productsAnimation.finished
      .then(() => {
        if (this._productsOpen) return;
        this.productsEl.classList.add('hidden');
        this.releaseProductsLayout();
        this._productsAnimation = null;
      })
      .catch(() => {
        this._productsAnimation = null;
      });
  }
}

if (!customElements.get('lookbook-carousel-mobile')) {
  customElements.define('lookbook-carousel-mobile', LookbookCarouselMobile);
}